(function () {
  'use strict';
  const sizes = new Map();

  class ChuppsProductPurchase extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-type="product"]');
      if (!this.section) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.onChange = (event) => {
        if (!this.section.contains(event.target)) return;
        const control = event.target.closest('[data-size-option] input');
        if (control) sizes.set(this.dataset.productId, control.dataset.originalValue || control.value);
        this.update();
      };
      document.addEventListener('change', this.onChange, { capture: true, signal });
      this.section.addEventListener('click', (event) => {
        if (event.target.closest('[data-chupps-select-size]')) this.focusSizes();
        const purchase = event.target.closest('[data-add-to-cart], [name="sr-headless-button"], .shopify-payment-button');
        if (purchase && (this.busy || !this.hasSize())) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!this.busy) this.focusSizes();
        }
      }, { capture: true, signal });
      document.addEventListener('submit', (event) => {
        if (event.target !== this.form) return;
        if (this.busy || !this.hasSize()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!this.busy) this.focusSizes();
        }
      }, { capture: true, signal });
      window.addEventListener('theme:section:navigate', (event) => {
        if (event.detail?.sectionId === this.section.dataset.sectionId) { this.busy = true; this.update(); }
      }, { signal });
      window.addEventListener('theme:section:update', (event) => {
        if (event.detail?.sectionId === this.section.dataset.sectionId) { this.busy = false; this.update(); this.observeButton(); }
      }, { signal });
      this.querySelector('[data-sticky-submit]').addEventListener('click', () => {
        if (!this.hasSize()) { this.focusSizes(); return; }
        if (!this.busy && this.form && this.mainButton && !this.mainButton.disabled) this.form.requestSubmit(this.mainButton);
      }, { signal });
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.section, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'data-available'] });
      this.stickyResize = new ResizeObserver(() => this.updateStickyHeight());
      this.stickyResize.observe(this);
      this.update();
      this.observeButton();
    }

    get form() { return this.section.querySelector('form[data-product-form]'); }
    get mainButton() { return this.form?.querySelector('[data-add-to-cart]'); }
    get sizeInputs() { return [...this.section.querySelectorAll('[data-size-option] input[type="radio"]')]; }

    hasSize() {
      return !this.sizeInputs.length || this.sizeInputs.some((input) => input.checked && !input.disabled);
    }

    focusSizes() {
      const target = this.sizeInputs.find((input) => !input.disabled);
      target?.closest('[data-size-option]')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      target?.focus({ preventScroll: true });
    }

    update() {
      if (!this.isConnected) return;
      const chosen = sizes.get(this.dataset.productId);
      if (chosen) this.sizeInputs.forEach((input) => { input.checked = !input.disabled && (input.dataset.originalValue || input.value) === chosen; });
      const selected = this.hasSize();
      const prompt = this.form?.querySelector('[data-chupps-select-size]');
      const selectable = !this.sizeInputs.length || this.sizeInputs.some((input) => !input.disabled);
      if (prompt) {
        prompt.hidden = selected;
        const promptLabel = selectable ? this.dataset.selectLabel : this.dataset.soldLabel;
        if (prompt.textContent !== promptLabel) prompt.textContent = promptLabel;
        const promptDisabled = !selectable || Boolean(this.busy);
        if (prompt.disabled !== promptDisabled) prompt.disabled = promptDisabled;
      }
      if (this.mainButton) this.mainButton.style.display = selected ? '' : 'none';
      const button = this.querySelector('[data-sticky-submit]');
      const available = this.dataset.available === 'true';
      const label = selected ? (available ? this.dataset.addLabel : this.dataset.soldLabel) : (selectable ? this.dataset.selectLabel : this.dataset.soldLabel);
      if (button.textContent !== label) button.textContent = label;
      const disabled = Boolean(this.busy || !selectable || (selected && (!available || this.mainButton?.disabled)));
      if (button.disabled !== disabled) button.disabled = disabled;
      const size = this.querySelector('[data-sticky-size]');
      const value = selected ? this.sizeInputs.find((input) => input.checked)?.dataset.originalValue || '' : '';
      const sizeLabel = value ? `${this.dataset.sizeLabel}: ${value}` : '';
      if (size.textContent !== sizeLabel) size.textContent = sizeLabel;
      size.hidden = !value;
    }

    updateStickyHeight() {
      if (!this.hidden) document.documentElement.style.setProperty('--chupps-sticky-height', `${this.offsetHeight}px`);
    }

    observeButton() {
      const target = this.form?.querySelector('[data-buttons-wrapper]');
      if (!target || target === this.observedButton) return;
      this.intersection?.disconnect();
      this.observedButton = target;
      this.intersection = new IntersectionObserver(([entry]) => {
        this.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
        this.updateStickyHeight();
        document.documentElement.classList.toggle('chupps-sticky-visible', !this.hidden);
      });
      this.intersection.observe(target);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      this.stickyResize?.disconnect();
      document.documentElement.style.removeProperty('--chupps-sticky-height');
      document.documentElement.classList.remove('chupps-sticky-visible');
    }
  }

  if (!customElements.get('chupps-product-purchase')) customElements.define('chupps-product-purchase', ChuppsProductPurchase);
})();
